(() => {
  const configEl = document.getElementById('eom-checkout-gate-config');
  if (!configEl) return;

  let config = {};
  try {
    config = JSON.parse(configEl.textContent || '{}');
  } catch (error) {
    console.warn('[EOM checkout gate] Invalid config JSON.', error);
    return;
  }

  if (!config.enabled) return;

  const maxQuantity = Number(config.maxQuantity || 10);
  if (!Number.isFinite(maxQuantity) || maxQuantity < 1) return;

  const nativeFetch = window.fetch.bind(window);
  const savedStorageKey = 'eom-save-for-later-v1';
  const selectedStorageKey = 'eom-checkout-selected-v1';

  let currentCart = null;
  let currentState = null;
  let refreshTimer = null;
  let observerTimer = null;
  let observer = null;
  let rendering = false;
  let busyAction = false;
  const checkoutControlOriginalText = new WeakMap();

  const checkoutSelector = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'button#checkout',
    'input#checkout',
    'a[href="/checkout"]',
    'a[href*="/checkout"]',
    '[formaction="/checkout"]',
    '[formaction*="/checkout"]'
  ].join(',');

  function log(...args) {
    if (config.debug) console.log('[EOM checkout gate]', ...args);
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function readStorage(key, fallback = []) {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(key) || JSON.stringify(fallback));
      return Array.isArray(fallback) ? (Array.isArray(parsed) ? parsed : fallback) : parsed;
    } catch {
      return fallback;
    }
  }

  function writeStorage(key, value) {
    window.localStorage.setItem(key, JSON.stringify(value));
  }

  function readSavedItems() {
    return readStorage(savedStorageKey, []);
  }

  function writeSavedItems(items) {
    writeStorage(savedStorageKey, items);
    document.dispatchEvent(new CustomEvent('eom:save-for-later:updated', { detail: { items } }));
  }

  function readSelectedKeys() {
    const quantities = readSelectedQuantities(currentCart);
    return new Set(Object.keys(quantities).filter((key) => Number(quantities[key] || 0) > 0));
  }

  function writeSelectedKeys(keys) {
    const next = {};
    const keySet = new Set(Array.from(keys || []));
    currentCart?.items?.forEach((item) => {
      next[item.key] = keySet.has(item.key) ? Number(item.quantity || 0) : 0;
    });
    writeSelectedQuantities(next);
  }

  function readSelectedRaw() {
    try {
      const stored = window.localStorage.getItem(selectedStorageKey);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  function writeSelectedQuantities(quantities) {
    writeStorage(selectedStorageKey, quantities || {});
  }

  function getDefaultSelectedQuantities(cart = currentCart) {
    const quantities = {};
    let remaining = maxQuantity;

    cart?.items?.forEach((item) => {
      const lineQuantity = Number(item.quantity || 0);
      const selectedQuantity = Math.max(0, Math.min(lineQuantity, remaining));
      quantities[item.key] = selectedQuantity;
      remaining -= selectedQuantity;
    });

    return quantities;
  }

  function readSelectedQuantities(cart = currentCart) {
    if (!cart?.items?.length) return {};

    const raw = readSelectedRaw();
    const defaults = getDefaultSelectedQuantities(cart);
    const next = {};

    cart.items.forEach((item) => {
      const maxLineQuantity = Number(item.quantity || 0);
      let selectedQuantity;

      if (raw === null) {
        selectedQuantity = defaults[item.key];
      } else if (Array.isArray(raw)) {
        selectedQuantity = raw.includes(item.key) ? maxLineQuantity : 0;
      } else if (typeof raw === 'object') {
        selectedQuantity = raw[item.key];
        if (selectedQuantity === undefined) selectedQuantity = defaults[item.key];
      } else {
        selectedQuantity = defaults[item.key];
      }

      selectedQuantity = Math.max(0, Math.min(maxLineQuantity, Number(selectedQuantity || 0)));
      next[item.key] = selectedQuantity;
    });

    return next;
  }

  function getItemCount(cart) {
    return Number(cart?.item_count || 0);
  }

  function getSelectedSummary(cart = currentCart) {
    const quantities = readSelectedQuantities(cart);
    const keys = [];
    const items = [];
    let count = 0;

    if (!cart?.items?.length) return { keys, items, quantities: {}, count };

    cart.items.forEach((item) => {
      const quantity = Math.max(0, Math.min(Number(item.quantity || 0), Number(quantities[item.key] || 0)));
      if (quantity > 0) {
        keys.push(item.key);
        items.push({ key: item.key, quantity });
        count += quantity;
      }
    });

    return { keys, items, quantities, count };
  }

  function reconcileSelectedKeys(cart) {
    if (!cart?.items?.length) {
      writeSelectedQuantities({});
      return;
    }

    const quantities = readSelectedQuantities(cart);
    const next = {};
    cart.items.forEach((item) => {
      next[item.key] = Math.max(0, Math.min(Number(item.quantity || 0), Number(quantities[item.key] || 0)));
    });

    const selectedCount = Object.values(next).reduce((sum, quantity) => sum + Number(quantity || 0), 0);
    if (cart.item_count > maxQuantity && selectedCount === cart.item_count) {
      writeSelectedQuantities(getDefaultSelectedQuantities(cart));
      return;
    }

    writeSelectedQuantities(next);
  }

  function buildState(cart) {
    const itemCount = getItemCount(cart);
    const selected = getSelectedSummary(cart);

    return {
      cart,
      itemCount,
      maxQuantity,
      overLimit: itemCount > maxQuantity,
      remainingUntilLimit: Math.max(0, maxQuantity - itemCount),
      overBy: Math.max(0, itemCount - maxQuantity),
      selectedCount: selected.count,
      selectedKeys: selected.keys,
      selectedAllowed: selected.count >= 1 && selected.count <= maxQuantity,
      updatedAt: new Date().toISOString()
    };
  }

  function dispatchState(state) {
    currentState = state;
    document.dispatchEvent(new CustomEvent('eom:checkout-gate:updated', { detail: state }));
    log('updated', state);
  }

  async function fetchCart() {
    const response = await nativeFetch('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });

    if (!response.ok) throw new Error(`Unable to read cart. Status: ${response.status}`);
    return response.json();
  }

  async function refreshCartState(reason = 'manual') {
    try {
      currentCart = await fetchCart();
      reconcileSelectedKeys(currentCart);
      const state = buildState(currentCart);
      state.reason = reason;
      renderAll(state);
      dispatchState(state);
      return state;
    } catch (error) {
      console.warn('[EOM checkout gate] Cart monitor refresh failed.', error);
      document.dispatchEvent(new CustomEvent('eom:checkout-gate:error', {
        detail: { error, reason, updatedAt: new Date().toISOString() }
      }));
      return null;
    }
  }

  function scheduleRefresh(reason = 'scheduled', delay = 150) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => refreshCartState(reason), delay);
  }

  function isCartMutationUrl(input) {
    try {
      const url = typeof input === 'string'
        ? new URL(input, window.location.origin)
        : input instanceof Request
          ? new URL(input.url, window.location.origin)
          : input instanceof URL
            ? input
            : null;
      return Boolean(url && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname));
    } catch {
      return false;
    }
  }

  function ensureStyles() {
    if (document.getElementById('eom-checkout-gate-styles')) return;

    const style = document.createElement('style');
    style.id = 'eom-checkout-gate-styles';
    style.textContent = `
      html.eom-checkout-gate-active {
        --eom-min-product-area: 170px;
      }

      html.eom-checkout-gate-active dialog.cart-drawer__dialog,
      html.eom-checkout-gate-active .cart-drawer,
      html.eom-checkout-gate-active cart-drawer,
      html.eom-checkout-gate-active .cart-drawer__inner {
        max-height: 100dvh;
      }

      .eom-checkout-gate-footer-tools {
        display: grid;
        gap: 8px;
        width: 100%;
        max-height: min(34vh, 245px);
        overflow-y: auto;
        overscroll-behavior: contain;
      }

      .eom-checkout-gate-banner,
      .eom-save-for-later,
      .eom-checkout-selected {
        width: 100%;
        margin: 0;
        padding: 10px 12px;
        border-radius: 8px;
        font-size: 13px;
        line-height: 1.35;
      }

      .eom-checkout-gate-banner {
        display: none;
        border: 1px solid #f3e5d2;
        background: #fff7ed;
        color: #8f4b18;
      }

      .cart-drawer__header .eom-checkout-gate-banner,
      dialog.cart-drawer__dialog .eom-checkout-gate-banner,
      .cart-drawer .eom-checkout-gate-banner {
        flex: 1 1 auto;
        width: auto;
        max-width: none;
        min-width: 0;
        margin: 0 20px;
        padding: 0 10px 0;
        border: 0;
        border-radius: 8px;
        background: transparent;
        color: #8f4b18;
        text-align: left;
        font-size: 12px;
        line-height: 1.12;
        max-height: 46px;
        overflow: hidden;
        align-self: center;
      }

      .cart-drawer__header {
        display: flex;
        align-items: center;
      }

      .eom-checkout-gate-banner.is-active,
      .eom-save-for-later.is-active,
      .eom-checkout-selected.is-active {
        display: block;
      }

      .eom-save-for-later,
      .eom-checkout-selected {
        display: none;
      }

      .eom-save-for-later {
        border: 1px solid #dce5e0;
        background: #f7fbf8;
        color: #142221;
        width: 100%;
        max-width: 100%;
        padding: 4px 8px;
        font-size: 12px;
      }

      .eom-save-for-later summary {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        align-items: center;
        cursor: pointer;
        list-style: none;
      }

      .eom-save-for-later[open] {
        width: 100%;
        padding: 8px 10px;
      }

      .eom-save-for-later summary::-webkit-details-marker {
        display: none;
      }

      .eom-save-for-later summary span {
        color: #5d6a66;
        font-size: 11px;
        font-weight: 800;
      }

      .eom-save-for-later__heading {
        font-size: 12px;
        line-height: 1.15;
      }

      .eom-checkout-selected {
        border: 1px solid #c5dced;
        background: #eaf4fb;
        color: #245f82;
      }

      .eom-checkout-gate-banner strong,
      .eom-save-for-later__heading,
      .eom-checkout-selected__heading {
        display: block;
        margin: 0 0 2px;
        color: #142221;
        font-size: 13px;
        font-weight: 900;
        line-height: 1.25;
      }

      .cart-drawer__header .eom-checkout-gate-banner strong,
      dialog.cart-drawer__dialog .eom-checkout-gate-banner strong,
      .cart-drawer .eom-checkout-gate-banner strong {
        margin: 0 0 1px;
        color: #8f4b18;
        font-size: 11px;
        line-height: 1;
        white-space: nowrap;
      }

      .cart-drawer__header .eom-checkout-gate-banner span,
      dialog.cart-drawer__dialog .eom-checkout-gate-banner span,
      .cart-drawer .eom-checkout-gate-banner span {
        display: block;
        color: #8f4b18;
        font-size: 11px;
        font-weight: 700;
        line-height: 1.1;
      }

      .eom-checkout-selected__text {
        margin: 0 0 8px;
      }

      .eom-checkout-gate-modal {
        display: none;
        position: fixed;
        inset: 0;
        z-index: 2147483647;
      }

      .eom-checkout-gate-modal.is-open {
        display: block;
      }

      .eom-checkout-gate-modal__backdrop {
        position: absolute;
        inset: 0;
        background: rgba(20, 34, 33, 0.48);
      }

      .eom-checkout-gate-modal__dialog {
        position: relative;
        width: min(92vw, 460px);
        margin: 12vh auto 0;
        background: #fff;
        color: #142221;
        border-radius: 8px;
        padding: 24px;
        box-shadow: 0 18px 60px rgba(0, 0, 0, 0.22);
      }

      .eom-checkout-gate-modal__dialog h2 {
        margin: 0 0 10px;
        font-size: 22px;
        line-height: 1.2;
        letter-spacing: 0;
      }

      .eom-checkout-gate-modal__dialog p {
        margin: 0;
        color: #5d6a66;
        line-height: 1.5;
      }

      .eom-checkout-gate-modal__actions {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: 8px;
        margin-top: 22px;
      }

      .eom-checkout-gate-modal__actions a,
      .eom-checkout-gate-modal__actions button,
      .eom-save-for-later__restore,
      .eom-save-for-later__remove,
      .eom-save-for-later-button,
      .eom-checkout-selected__button {
        border-radius: 6px;
        border: 1px solid #2f7d42;
        padding: 9px 12px;
        font: inherit;
        font-weight: 800;
        cursor: pointer;
        background: #2f7d42;
        color: #fff;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .eom-save-for-later-button,
      .eom-save-for-later__remove,
      .eom-checkout-gate-modal__actions button {
        background: #fff;
        color: #2f7d42;
      }

      .eom-save-for-later-button {
        margin-top: 8px;
        padding: 8px 11px;
        font-size: 13px;
      }

      html.eom-checkout-gate-active .cart-drawer__items,
      html.eom-checkout-gate-active .cart-drawer__body {
        min-height: min(var(--eom-min-product-area), 36vh);
      }

      html.eom-checkout-gate-active .cart-drawer__footer,
      html.eom-checkout-gate-active .cart__footer,
      html.eom-checkout-gate-active .cart-summary,
      html.eom-checkout-gate-active .cart-summary__inner {
        flex: 0 0 auto;
      }

      html.eom-checkout-gate-active .cart-drawer__footer,
      html.eom-checkout-gate-active .cart-summary,
      html.eom-checkout-gate-active .cart-summary__inner {
        gap: 10px !important;
      }

      html.eom-checkout-gate-active .cart-drawer__footer > *,
      html.eom-checkout-gate-active .cart-summary > *,
      html.eom-checkout-gate-active .cart-summary__inner > * {
        margin-top: 0 !important;
        margin-bottom: 4px !important;
      }

      html.eom-checkout-gate-active .cart-drawer__summary,
      html.eom-checkout-gate-active .cart-drawer__footer,
      html.eom-checkout-gate-active .cart-summary,
      html.eom-checkout-gate-active .cart-summary__inner,
      html.eom-checkout-gate-active .cart__footer {
        gap: 4px !important;
        padding-top: 4px !important;
        padding-bottom: 4px !important;
      }

      html.eom-checkout-gate-active .cart-totals,
      html.eom-checkout-gate-active .cart-totals__item,
      html.eom-checkout-gate-active .cart-totals__tax-note,
      html.eom-checkout-gate-active .cart-primary-typography {
        margin-top: 0 !important;
        margin-bottom: 2px !important;
      }

      html.eom-checkout-gate-active .cart__ctas {
        margin-top: 0;
        margin-bottom: 2px !important;
        gap: 6px !important;
      }

      html.eom-checkout-gate-active .cart-drawer__footer button[name="checkout"],
      html.eom-checkout-gate-active .cart-drawer__footer #checkout,
      html.eom-checkout-gate-active .cart-summary button[name="checkout"],
      html.eom-checkout-gate-active .cart-summary #checkout,
      html.eom-checkout-gate-active .cart-summary__inner button[name="checkout"],
      html.eom-checkout-gate-active .cart-summary__inner #checkout {
        margin-top: 4px !important;
        margin-bottom: 2px !important;
      }

      html.eom-checkout-gate-active .additional-checkout-buttons,
      html.eom-checkout-gate-active .shopify-payment-button,
      html.eom-checkout-gate-active shopify-accelerated-checkout,
      html.eom-checkout-gate-active shopify-accelerated-checkout-cart {
        margin-top: 0 !important;
      }

      html.eom-checkout-gate-active dialog.cart-drawer__dialog shopify-payment-terms,
      html.eom-checkout-gate-active .cart-drawer shopify-payment-terms,
      html.eom-checkout-gate-active dialog.cart-drawer__dialog .shopify-payment-terms,
      html.eom-checkout-gate-active .cart-drawer .shopify-payment-terms,
      html.eom-checkout-gate-active dialog.cart-drawer__dialog [data-shopify="payment-terms"],
      html.eom-checkout-gate-active .cart-drawer [data-shopify="payment-terms"],
      html.eom-checkout-gate-active dialog.cart-drawer__dialog .shopify-installments,
      html.eom-checkout-gate-active .cart-drawer .shopify-installments,
      html.eom-checkout-gate-active dialog.cart-drawer__dialog .product-form__payment-terms,
      html.eom-checkout-gate-active .cart-drawer .product-form__payment-terms,
      html.eom-checkout-gate-active dialog.cart-drawer__dialog .cart__payment-terms,
      html.eom-checkout-gate-active .cart-drawer .cart__payment-terms,
      html.eom-checkout-gate-active .cart-drawer__footer shopify-payment-terms,
      html.eom-checkout-gate-active .cart-drawer__footer .shopify-payment-terms,
      html.eom-checkout-gate-active .cart-drawer__footer [data-shopify="payment-terms"],
      html.eom-checkout-gate-active .cart-summary shopify-payment-terms,
      html.eom-checkout-gate-active .cart-summary .shopify-payment-terms,
      html.eom-checkout-gate-active .cart-summary [data-shopify="payment-terms"],
      html.eom-checkout-gate-active .cart-summary__inner shopify-payment-terms,
      html.eom-checkout-gate-active .cart-summary__inner .shopify-payment-terms,
      html.eom-checkout-gate-active .cart-summary__inner [data-shopify="payment-terms"] {
        display: none !important;
      }

      .eom-action-busy {
        opacity: 0.7;
        cursor: wait;
      }

      .eom-save-for-later__list {
        display: grid;
        gap: 12px;
        padding: 10px;
      }

      .eom-save-for-later__item {
        display: grid;
        grid-template-columns: 56px minmax(0, 1fr);
        gap: 12px;
        align-items: center;
        padding: 12px;
        border: 1px solid #dce5e0;
        border-radius: 8px;
        background: #fff;
      }

      .eom-save-for-later__image {
        width: 56px;
        height: 56px;
        object-fit: cover;
        border-radius: 6px;
        background: #eef3ef;
      }

      .eom-save-for-later__title {
        margin: 0 0 4px;
        font-weight: 800;
        line-height: 1.3;
      }

      .eom-save-for-later__meta {
        margin: 0 0 10px;
        color: #5d6a66;
        font-size: 13px;
      }

      .eom-save-for-later__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;
      }

      .eom-line-control {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 0;
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        grid-column: 1 / -1;
        flex: 0 0 100%;
        margin-top: 8px;
        border: 1px solid #dce5e0;
        border-radius: 8px;
        background: #f8fbf9;
        position: relative;
        z-index: 2;
        font-size: 13px;
        line-height: 1.2;
        color: #142221;
        overflow: visible;
      }

      .eom-line-control__row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        align-items: center;
        gap: 10px;
        width: 100%;
        min-width: 0;
        min-height: 42px;
        padding: 7px 8px;
      }

      .eom-line-control__row + .eom-line-control__row {
        border-top: 1px solid rgba(20, 34, 33, 0.08);
      }

      .eom-line-select {
        display: inline-flex;
        gap: 7px;
        align-items: center;
        color: #142221;
        font-size: 13px;
        font-weight: 850;
        min-width: 0;
        max-width: 100%;
      }

      .eom-line-select input {
        width: 16px;
        height: 16px;
        accent-color: #2f7d42;
      }

      .eom-line-quantity {
        display: grid;
        grid-template-columns: auto auto;
        align-items: center;
        gap: 8px;
        color: #142221;
        font-size: 13px;
        font-weight: 850;
        min-width: 0;
        max-width: 100%;
      }

      .eom-cart-dropdown {
        position: relative;
        min-width: 0;
        user-select: none;
      }

      .eom-cart-dropdown__trigger {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        width: 64px;
        height: 36px;
        padding: 0 10px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        background: #fff;
        color: #1a2a2a;
        font: inherit;
        font-size: 15px;
        font-weight: 900;
        cursor: pointer;
        transition: border-color 0.18s ease, border-radius 0.18s ease;
      }

      .eom-cart-dropdown__trigger:hover,
      .eom-cart-dropdown.is-open .eom-cart-dropdown__trigger {
        border-color: #2f7d42;
      }

      .eom-cart-dropdown.is-open .eom-cart-dropdown__trigger {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }

      .eom-cart-dropdown__arrow {
        width: 15px;
        height: 15px;
        flex: 0 0 auto;
        transition: transform 0.18s ease;
      }

      .eom-cart-dropdown.is-open .eom-cart-dropdown__arrow {
        transform: rotate(180deg);
      }

      .eom-cart-dropdown__list {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        z-index: 20;
        max-height: 190px;
        overflow-y: auto;
        margin: 0;
        padding: 0;
        list-style: none;
        border: 1px solid #2f7d42;
        border-top: none;
        border-radius: 0 0 8px 8px;
        background: #fff;
        opacity: 0;
        visibility: hidden;
        transform: translateY(-4px);
        transition: opacity 0.18s ease, visibility 0.18s ease, transform 0.18s ease;
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.10);
      }

      .eom-cart-dropdown.is-open .eom-cart-dropdown__list {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
      }

      .eom-cart-dropdown__item {
        padding: 10px 12px;
        border-bottom: 1px solid #f5f5f5;
        color: #1a2a2a;
        cursor: pointer;
        font-size: 14px;
        font-weight: 700;
        transition: background-color 0.15s ease, color 0.15s ease, padding-left 0.15s ease;
      }

      .eom-cart-dropdown__item:last-child {
        border-bottom: none;
      }

      .eom-cart-dropdown__item:hover {
        background-color: #f4f9f4;
        color: #2f7d42;
        padding-left: 16px;
      }

      .eom-cart-dropdown__item.is-selected {
        background-color: #f4f9f4;
        color: #2f7d42;
        font-weight: 900;
      }

      .eom-line-quantity span,
      .eom-line-select span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .eom-line-save-link {
        height: 36px;
        margin: 0;
        padding: 0 11px;
        border: 1px solid rgba(47, 125, 66, 0.35);
        border-radius: 8px;
        background: #fff;
        color: #2f7d42;
        font: inherit;
        font-size: 13px;
        font-weight: 900;
        line-height: 1.2;
        text-decoration: none;
        cursor: pointer;
        white-space: nowrap;
      }

      .eom-line-save-link:hover {
        border-color: #2f7d42;
        background: #f4f9f4;
      }

      .eom-line-save-row {
        color: #5d6a66;
      }

      .eom-line-save-row .eom-line-quantity {
        color: #5d6a66;
      }

      .eom-line-action {
        display: inline-grid;
        grid-template-columns: auto auto;
        align-items: center;
        gap: 8px;
        color: #5d6a66;
        font-size: 12px;
        font-weight: 850;
        white-space: nowrap;
      }

      .eom-cart-title {
        display: inline-flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
        max-width: 100%;
        min-width: 0;
        color: inherit;
        line-height: 1;
        vertical-align: middle;
        overflow: visible;
      }

      .eom-cart-title-link {
        display: block !important;
        width: 100%;
        overflow: visible !important;
        line-height: 1 !important;
        text-decoration: none;
      }

      .eom-cart-title-link:hover .eom-cart-title__type {
        text-decoration: underline;
        text-decoration-thickness: 1.5px;
        text-underline-offset: 2px;
      }

      .eom-cart-title-wrap {
        margin-top: 0 !important;
        margin-bottom: 4px !important;
        overflow: visible !important;
        line-height: 1 !important;
      }

      .eom-cart-title__header {
        display: grid;
        gap: 1px;
        max-width: 100%;
        min-width: 0;
      }

      .eom-cart-title__brand {
        display: inline-flex;
        align-items: center;
        flex: 0 0 auto;
        height: 14px;
        min-height: 14px;
        max-width: 90px;
        overflow: visible;
      }

      .eom-cart-title__brand svg {
        display: block;
        flex-shrink: 0;
        overflow: visible;
        width: auto;
        height: 12px;
        max-height: 12px;
      }

      .eom-cart-title__brand-text {
        margin: 0;
        color: #050505;
        font-size: 13px;
        line-height: 1;
        font-weight: 860;
        letter-spacing: -0.045em;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .eom-cart-title__type {
        display: block;
        max-width: 100%;
        min-width: 0;
        color: #1a2a2a;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 17px;
        line-height: 1.04;
        font-weight: 870;
        letter-spacing: -0.045em;
      }

      .eom-cart-title__specs {
        display: inline-flex;
        align-items: center;
        flex-wrap: nowrap;
        gap: 5px;
        max-width: 100%;
        color: #142221;
        overflow: hidden;
      }

      .eom-cart-title__spec {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 19px;
        padding: 3px 8px;
        border: 1px solid #dce5e0;
        border-radius: 999px;
        background: #fff;
        color: #142221;
        font-size: 11px;
        line-height: 1;
        font-weight: 800;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      @media (max-width: 520px) {
        .eom-save-for-later__item {
          grid-template-columns: 48px minmax(0, 1fr);
        }

        .eom-save-for-later__image {
          width: 48px;
          height: 48px;
        }

        .eom-save-for-later__actions button,
        .eom-checkout-selected__button,
        .eom-checkout-gate-modal__actions a,
        .eom-checkout-gate-modal__actions button {
          width: 100%;
        }
      }

      @media (max-height: 720px) {
        .eom-checkout-gate-footer-tools {
          max-height: min(30vh, 190px);
        }

        html.eom-checkout-gate-over-limit .shopify-payment-button,
        html.eom-checkout-gate-over-limit shopify-accelerated-checkout,
        html.eom-checkout-gate-over-limit shopify-accelerated-checkout-cart,
        html.eom-checkout-gate-over-limit .dynamic-checkout,
        html.eom-checkout-gate-over-limit .additional-checkout-buttons {
          display: none !important;
        }

        .eom-checkout-gate-banner,
        .eom-checkout-selected {
          padding: 8px 10px;
          font-size: 12px;
        }

        .eom-checkout-gate-banner strong,
        .eom-checkout-selected__heading {
          font-size: 12px;
        }
      }

      @media (max-height: 580px) {
        .eom-checkout-gate-footer-tools {
          max-height: 26vh;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function getCheckoutControl() {
    return document.querySelector('button[name="checkout"], input[name="checkout"], button#checkout, input#checkout, a[href="/checkout"], a[href*="/checkout"]');
  }

  function getCheckoutControls() {
    return Array.from(document.querySelectorAll(checkoutSelector)).filter((control) => {
      return control instanceof Element && !control.closest('[data-eom-checkout-gate-ignore]');
    });
  }

  function getCheckoutControlText(control) {
    if (control instanceof HTMLInputElement) return control.value || '';
    return (control.textContent || '').trim();
  }

  function setCheckoutControlText(control, text) {
    if (!control || !text) return;

    if (!checkoutControlOriginalText.has(control)) {
      checkoutControlOriginalText.set(control, getCheckoutControlText(control));
    }

    if (control instanceof HTMLInputElement) {
      control.value = text;
      return;
    }

    const labelTarget = control.querySelector('[data-eom-checkout-label]')
      || control.querySelector('.button__text')
      || control.querySelector('span:not(.svg-wrapper)')
      || control;

    labelTarget.textContent = text;
  }

  function restoreCheckoutControlText(control) {
    if (!control || !checkoutControlOriginalText.has(control)) return;
    const originalText = checkoutControlOriginalText.get(control);

    if (control instanceof HTMLInputElement) {
      control.value = originalText;
      return;
    }

    const labelTarget = control.querySelector('[data-eom-checkout-label]')
      || control.querySelector('.button__text')
      || control.querySelector('span:not(.svg-wrapper)')
      || control;

    labelTarget.textContent = originalText;
  }

  function getCheckoutActionText(state = currentState) {
    if (!state?.overLimit) return null;
    return state.selectedAllowed ? `Checkout selected (${state.selectedCount})` : 'Review selection';
  }

  function updateCheckoutControls(state = currentState) {
    getCheckoutControls().forEach((control) => {
      if (!state?.overLimit) {
        restoreCheckoutControlText(control);
        control.removeAttribute('data-eom-checkout-gate-action');
        return;
      }

      setCheckoutControlText(control, getCheckoutActionText(state));
      control.setAttribute('data-eom-checkout-gate-action', state.selectedAllowed ? 'checkout-selected' : 'review-selection');
    });
  }

  function removeLegacySelectedPanel() {
    const panel = document.getElementById('eom-checkout-selected-panel');
    if (panel) panel.remove();
  }

  function getFooterContainer() {
    const checkout = getCheckoutControl();

    return checkout?.closest('.cart-summary__inner, .cart-summary, form, footer, .cart__footer, .cart-drawer__footer, .cart-drawer__summary')
      || document.querySelector('.cart-summary__inner')
      || document.querySelector('.cart-summary')
      || checkout?.parentElement
      || document.querySelector('dialog.cart-drawer__dialog')
      || document.querySelector('.cart-drawer')
      || document.querySelector('main')
      || document.body;
  }

  function getSavedContainer() {
    return document.querySelector('cart-items-component')
      || document.querySelector('.cart-items')
      || document.querySelector('.cart__items')
      || document.querySelector('.cart-drawer__items')
      || document.querySelector('.cart-drawer__body')
      || document.querySelector('dialog.cart-drawer__dialog')
      || document.querySelector('.cart-drawer')
      || document.querySelector('main')
      || document.body;
  }

  function getCartProductAreas() {
    return Array.from(document.querySelectorAll([
      'cart-items-component',
      '.cart-items',
      '.cart__items',
      '.cart-drawer__items',
      '.cart-drawer__body'
    ].join(','))).filter((element) => element instanceof HTMLElement);
  }

  function isUsableProductArea(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (!document.body.contains(element)) return false;
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    return Boolean(
      element.querySelector('[data-cart-line], [data-cart-item], [data-line-item], [data-key], cart-line-item, .cart-item, .cart-items__item, .cart-drawer__item, .cart-line')
    );
  }

  function getPrimaryCartProductArea() {
    const preferredSelectors = [
      '.cart-drawer__body',
      '.cart-drawer__items',
      'cart-items-component',
      '.cart-items',
      '.cart__items'
    ];

    for (const selector of preferredSelectors) {
      const area = Array.from(document.querySelectorAll(selector)).find(isUsableProductArea);
      if (area) return area;
    }

    return null;
  }

  function clearForcedDrawerContainerLayout() {
    Array.from(document.querySelectorAll([
      'dialog.cart-drawer__dialog',
      '.cart-drawer',
      'cart-drawer',
      '.cart-drawer__inner'
    ].join(','))).forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      if (element.getAttribute('data-eom-layout-managed') !== 'true') return;
      element.style.display = '';
      element.style.flexDirection = '';
      element.style.minHeight = '';
      element.removeAttribute('data-eom-layout-managed');
    });
  }

  function updateProductAreaLayout(state = currentState) {
    const overLimit = Boolean(state?.overLimit);
    clearForcedDrawerContainerLayout();

    getCartProductAreas().forEach((element) => {
      if (element.getAttribute('data-eom-layout-managed') === 'true') {
        element.style.flex = '';
        element.style.minHeight = '';
        element.style.overflowY = '';
        element.style.overscrollBehavior = '';
        element.style.webkitOverflowScrolling = '';
      }
      element.removeAttribute('data-eom-layout-managed');
    });

    if (!overLimit) {
      return;
    }
    const primaryArea = getPrimaryCartProductArea();
    if (!primaryArea) {
      return;
    }

    primaryArea.setAttribute('data-eom-layout-managed', 'true');
    primaryArea.style.flex = '1 1 auto';
    primaryArea.style.minHeight = 'var(--eom-min-product-area)';
    primaryArea.style.overflowY = 'auto';
    primaryArea.style.overscrollBehavior = 'contain';
    primaryArea.style.webkitOverflowScrolling = 'touch';
  }

  function isSafariBrowser() {
    const ua = navigator.userAgent || '';
    return /Safari/i.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|Edg|OPR/i.test(ua);
  }

  function getWalletKind(config) {
    const name = String(config?.name || '').toLowerCase();
    if (name.includes('shop')) return 'shop_pay';
    if (name.includes('paypal')) return 'paypal';
    if (name.includes('apple')) return 'apple_pay';
    if (name.includes('google')) return 'google_pay';
    return name || 'other';
  }

  function getWalletPriority(kind) {
    const safari = isSafariBrowser();
    const order = safari
      ? { shop_pay: 1, paypal: 2, apple_pay: 3, google_pay: 4 }
      : { shop_pay: 1, paypal: 2, google_pay: 3, apple_pay: 4 };
    return order[kind] || 99;
  }

  function normalizeAcceleratedCheckoutWallets() {
    document.querySelectorAll('shopify-accelerated-checkout, shopify-accelerated-checkout-cart').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      if (element.getAttribute('data-eom-wallet-normalized') === 'true') return;

      const raw = element.getAttribute('wallet-configs');
      if (!raw) return;

      let configs;
      try {
        configs = JSON.parse(raw);
      } catch {
        return;
      }
      if (!Array.isArray(configs) || configs.length <= 3) {
        element.setAttribute('data-eom-wallet-normalized', 'true');
        return;
      }

      const selected = configs
        .map((config, index) => ({ config, index, kind: getWalletKind(config) }))
        .sort((a, b) => getWalletPriority(a.kind) - getWalletPriority(b.kind) || a.index - b.index)
        .slice(0, 3)
        .map((entry) => entry.config);

      const replacement = element.cloneNode(true);
      replacement.setAttribute('wallet-configs', JSON.stringify(selected));
      replacement.setAttribute('data-eom-wallet-normalized', 'true');
      element.replaceWith(replacement);
    });

    document.querySelectorAll('.additional-checkout-buttons').forEach((element) => {
      element.classList.remove('additional-checkout-buttons--vertical');
      element.classList.add('additional-checkout-buttons--horizontal');
    });
  }

  function ensureFooterTools() {
    let tools = document.getElementById('eom-checkout-gate-footer-tools');

    if (!tools || !document.body.contains(tools)) {
      tools = document.createElement('div');
      tools.id = 'eom-checkout-gate-footer-tools';
      tools.className = 'eom-checkout-gate-footer-tools';
    }

    const checkout = getCheckoutControl();
    if (checkout?.parentElement) {
      if (tools.parentElement !== checkout.parentElement || tools.nextElementSibling !== checkout) {
        checkout.parentElement.insertBefore(tools, checkout);
      }
    } else if (!tools.parentElement) {
      getFooterContainer().prepend(tools);
    }

    return tools;
  }

  function getCartHeaderContainer() {
    return document.querySelector('dialog.cart-drawer__dialog .cart-drawer__header')
      || document.querySelector('.cart-drawer .cart-drawer__header')
      || document.querySelector('cart-drawer .cart-drawer__header')
      || document.querySelector('.cart-drawer__header')
      || document.querySelector('.cart__header');
  }

  function ensureBanner() {
    let banner = document.getElementById('eom-checkout-gate-banner');
    if (!banner || !document.body.contains(banner)) {
      banner = document.createElement('div');
      banner.id = 'eom-checkout-gate-banner';
      banner.className = 'eom-checkout-gate-banner';
    }

    const header = getCartHeaderContainer();
    if (header) {
      const closeButton = Array.from(header.querySelectorAll('button, a')).find((control) => {
        const label = String(control.getAttribute('aria-label') || '').toLowerCase();
        return control.getAttribute('name') === 'close'
          || label.includes('close')
          || control.classList.contains('cart-drawer__close')
          || control.classList.contains('drawer__close');
      });
      if (closeButton?.parentElement === header) {
        if (banner.parentElement !== header || banner.nextElementSibling !== closeButton) {
          header.insertBefore(banner, closeButton);
        }
      } else if (banner.parentElement !== header) {
        header.appendChild(banner);
      }
    } else if (!banner.parentElement) {
      ensureFooterTools().appendChild(banner);
    }

    banner.setAttribute('role', 'status');
    banner.setAttribute('aria-live', 'polite');
    return banner;
  }

  function ensureSavedPanel() {
    let panel = document.getElementById('eom-save-for-later-panel');
    const container = getSavedContainer();

    if (!panel || !document.body.contains(panel)) {
      panel = document.createElement('details');
      panel.id = 'eom-save-for-later-panel';
      panel.className = 'eom-save-for-later';
      panel.setAttribute('aria-labelledby', 'eom-save-for-later-heading');
    }

    if (panel.parentElement !== container) container.appendChild(panel);
    return panel;
  }

  function updateBanner(state = currentState) {
    if (!state) return;
    removeLegacySelectedPanel();
    const banner = ensureBanner();
    banner.classList.toggle('is-active', state.overLimit);
    updateCheckoutControls(state);

    if (!state.overLimit) {
      banner.innerHTML = '';
      return;
    }

    banner.innerHTML = `
      <strong>${state.itemCount} items / limit ${maxQuantity}</strong>
      <span>Select checkout quantities<br>or save extras for later.</span>
    `;
  }

  function renderSelectedPanel() {
    updateBanner(currentState);
  }

  function ensureModal() {
    let modal = document.getElementById('eom-checkout-gate-modal');
    if (modal) return modal;

    modal = document.createElement('div');
    modal.id = 'eom-checkout-gate-modal';
    modal.className = 'eom-checkout-gate-modal';
    modal.innerHTML = `
      <div class="eom-checkout-gate-modal__backdrop" data-eom-checkout-gate-close></div>
      <div class="eom-checkout-gate-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="eom-checkout-gate-title">
        <h2 id="eom-checkout-gate-title"></h2>
        <p id="eom-checkout-gate-message"></p>
        <div class="eom-checkout-gate-modal__actions">
          <button type="button" data-eom-checkout-gate-close>Stay here</button>
          <a href="/cart" data-eom-cart-cta>Go to cart</a>
        </div>
      </div>
    `;
    document.body.appendChild(modal);

    modal.addEventListener('click', (event) => {
      if (event.target.closest('[data-eom-checkout-gate-close]')) {
        modal.classList.remove('is-open');
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') modal.classList.remove('is-open');
    });
    return modal;
  }

  function showModal(title, text, cartCta = true) {
    const modal = ensureModal();
    modal.querySelector('#eom-checkout-gate-title').textContent = title;
    modal.querySelector('#eom-checkout-gate-message').textContent = text;
    modal.querySelector('[data-eom-cart-cta]').style.display = cartCta ? '' : 'none';
    modal.classList.add('is-open');
    modal.querySelector('[data-eom-checkout-gate-close]').focus();
  }

  function showBlockedModal(state = currentState) {
    if (!state) return;
    showModal(
      'Checkout limit reached',
      `Your cart currently has ${state.itemCount} items. Please go to your cart and save items for later, or select up to ${maxQuantity} items to checkout.`,
      true
    );
  }

  function normalizeSavedItem(item) {
    return {
      key: item.key,
      variant_id: item.variant_id,
      product_id: item.product_id,
      quantity: item.quantity,
      vendor: item.vendor,
      product_type: item.product_type,
      title: item.product_title || item.title,
      variant_title: item.variant_title,
      url: item.url,
      image: item.image,
      properties: item.properties || {},
      selling_plan: item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || null,
      savedAt: new Date().toISOString()
    };
  }

  function saveItemLocally(cartItem) {
    const saved = readSavedItems();
    const normalized = normalizeSavedItem(cartItem);
    const existingIndex = saved.findIndex((item) => {
      return String(item.variant_id) === String(normalized.variant_id)
        && JSON.stringify(item.properties || {}) === JSON.stringify(normalized.properties || {})
        && String(item.selling_plan || '') === String(normalized.selling_plan || '');
    });

    if (existingIndex >= 0) {
      saved[existingIndex].quantity = Number(saved[existingIndex].quantity || 0) + Number(normalized.quantity || 0);
      saved[existingIndex].savedAt = normalized.savedAt;
    } else {
      saved.unshift(normalized);
    }
    writeSavedItems(saved);
  }

  function saveItemsLocally(items) {
    items.forEach(saveItemLocally);
  }

  function removeSavedItem(index) {
    const saved = readSavedItems();
    saved.splice(index, 1);
    writeSavedItems(saved);
  }

  function renderSavedPanel() {
    const saved = readSavedItems();
    const panel = ensureSavedPanel();
    const shouldShow = saved.length > 0;
    panel.classList.toggle('is-active', shouldShow);

    if (!shouldShow) {
      panel.innerHTML = '';
      return;
    }

    panel.innerHTML = `
      <summary id="eom-save-for-later-heading" class="eom-save-for-later__heading">Saved for later <span>${saved.length} item${saved.length === 1 ? '' : 's'}</span></summary>
      <div class="eom-save-for-later__list">
        ${saved.map((item, index) => `
          <article class="eom-save-for-later__item" data-eom-saved-index="${index}">
            ${item.image ? `<img class="eom-save-for-later__image" src="${escapeHtml(item.image)}" alt="">` : '<div class="eom-save-for-later__image" aria-hidden="true"></div>'}
            <div>
              <p class="eom-save-for-later__title">${escapeHtml(item.title)}</p>
              <p class="eom-save-for-later__meta">${item.variant_title ? `${escapeHtml(item.variant_title)} - ` : ''}Qty ${Number(item.quantity || 1)}</p>
              <div class="eom-save-for-later__actions">
                <button type="button" class="eom-save-for-later__restore" data-eom-restore-saved="${index}">Move back to cart</button>
                <button type="button" class="eom-save-for-later__remove" data-eom-remove-saved="${index}">Remove</button>
              </div>
            </div>
          </article>
        `).join('')}
      </div>
    `;
  }

  function findCartLineElements() {
    const candidates = Array.from(document.querySelectorAll([
      '[data-cart-line]',
      '[data-cart-item]',
      '[data-line-item]',
      '[data-key]',
      'cart-line-item',
      '.cart-item',
      '.cart-items__item',
      '.cart-drawer__item',
      '.cart-line',
      'tr'
    ].join(',')));

    return candidates.filter((element) => {
      if (!(element instanceof Element)) return false;
      return Boolean(
        element.querySelector('input[name="updates[]"]')
        || element.querySelector('[name="updates[]"]')
        || element.querySelector('a[href*="/products/"]')
        || element.matches('[data-cart-line], [data-cart-item], [data-line-item], [data-key], cart-line-item')
      );
    });
  }

  function cartLineMatchesItem(element, item, index) {
    const possibleValues = [item.key, item.id, item.variant_id, String(index + 1)]
      .map((value) => String(value || ''))
      .filter(Boolean);
    const attributes = [
      element.getAttribute('data-cart-line'),
      element.getAttribute('data-cart-item'),
      element.getAttribute('data-line-item'),
      element.getAttribute('data-key'),
      element.getAttribute('data-line')
    ].map((value) => String(value || '')).filter(Boolean);

    if (attributes.some((value) => possibleValues.includes(value))) return true;

    const productLink = element.querySelector('a[href*="/products/"]');
    if (productLink && item.url) {
      try {
        const elementUrl = new URL(productLink.getAttribute('href'), window.location.origin);
        const itemUrl = new URL(item.url, window.location.origin);
        if (elementUrl.pathname === itemUrl.pathname) return true;
      } catch {
        // Use text fallback.
      }
    }
    return Boolean(element.textContent?.includes(item.product_title || item.title));
  }

  function getControlTarget(lineElement) {
    return lineElement;
  }

  function compactWhitespace(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function getSeoTitle(item) {
    const title = compactWhitespace(item?.product_title || item?.title);
    const vendor = compactWhitespace(item?.vendor);
    if (!title) return vendor;
    if (!vendor) return title;
    return title.toLowerCase().includes(vendor.toLowerCase()) ? title : `${vendor} ${title}`;
  }

  function getBrandName(item) {
    const vendor = compactWhitespace(item?.vendor);
    if (vendor) return vendor;
    const title = compactWhitespace(item?.product_title || item?.title);
    if (/habitrol/i.test(title) || /habitrol/i.test(String(item?.image || ''))) return 'Habitrol';
    return '';
  }

  function normalizeBrandKey(value) {
    return compactWhitespace(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function readConfiguredBrandLogo(brand) {
    const logos = config.brandLogos || config.brand_logos || {};
    if (!logos || typeof logos !== 'object') return '';
    const logo = logos[brand] || logos[normalizeBrandKey(brand)] || '';
    return typeof logo === 'string' && logo.includes('<svg') ? logo : '';
  }

  function findRenderedBrandLogo(brand) {
    const brandKey = normalizeBrandKey(brand);
    const selectors = [
      '.nrt-brand-logo',
      '.rp-h-card__brand-svg',
      '.nrt-brand-svg-product-detail-page',
      '[class*="brand-logo"]',
      '[class*="brand-svg"]'
    ];

    for (const selector of selectors) {
      const nodes = Array.from(document.querySelectorAll(selector));
      for (const node of nodes) {
        const svg = node.querySelector('svg');
        if (brandKey === 'habitrol' && svg && /nrt-brand-logo|rp-h-card__brand-svg|nrt-brand-svg-product-detail-page/.test(node.className || '')) {
          return svg.outerHTML;
        }

        const text = compactWhitespace(node.textContent);
        const html = node.innerHTML || '';
        const nodeKey = normalizeBrandKey(`${node.getAttribute('data-brand') || ''} ${text} ${html}`);
        if (brandKey && nodeKey && !nodeKey.includes(brandKey) && !html.toLowerCase().includes(brand.toLowerCase())) continue;
        if (svg) return svg.outerHTML;
      }
    }

    return '';
  }

  function getBrandLogoHtml(brand) {
    if (!brand) return '';
    const configured = readConfiguredBrandLogo(brand);
    if (configured) return configured;
    return findRenderedBrandLogo(brand);
  }

  function getProductTypeFromTitle(title) {
    const normalized = compactWhitespace(title);
    if (/lozenge/i.test(normalized)) return 'Nicotine Lozenges';
    if (/patch|transdermal/i.test(normalized)) return 'Nicotine Patches';
    if (/gum/i.test(normalized)) return 'Nicotine Gum';
    return normalized.split(/\s+-\s+|,/)[0] || normalized;
  }

  function getTitleSpecs(item, productType) {
    const source = compactWhitespace([
      item?.product_title,
      item?.title,
      item?.variant_title,
      Array.isArray(item?.options_with_values)
        ? item.options_with_values.map((option) => option?.value).join(' ')
        : ''
    ].filter(Boolean).join(' '));
    const specs = [];

    const dosageMatch = source.match(/\b(\d+(?:\.\d+)?)\s*mg\b/i);
    if (dosageMatch) specs.push(`${dosageMatch[1]}mg`);

    const stepMatch = source.match(/\bstep\s*([1-3])\b/i);
    if (stepMatch) specs.push(`Step ${stepMatch[1]}`);

    const flavorCandidates = [
      'Mint', 'Fruit', 'Cherry', 'Cinnamon', 'Original', 'Unflavored',
      'Ice Mint', 'Orange', 'Grape', 'Lemon'
    ];
    const flavor = flavorCandidates.find((candidate) => {
      return new RegExp(`\\b${candidate.replace(/\s+/g, '\\s+')}\\b`, 'i').test(source);
    });
    if (flavor && !/patch/i.test(productType)) specs.push(flavor);

    const countMatch = source.match(/\b(\d+)\s*(pieces?|piece|count|ct|patches?|lozenges?|gum)\b/i);
    if (countMatch) {
      const countLabel = /patch/i.test(productType) ? 'count' : 'pieces';
      specs.push(`${countMatch[1]} ${countLabel}`);
    }

    const seen = new Set();
    return specs.filter((spec) => {
      const key = spec.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function buildCartTitleHtml(item) {
    const seoTitle = getSeoTitle(item);
    const brand = getBrandName(item);
    const productType = getProductTypeFromTitle(item?.product_title || item?.title || seoTitle);
    const specs = getTitleSpecs(item, productType).slice(0, 3);
    const brandLogo = getBrandLogoHtml(brand);
    const brandMarkup = brandLogo
      ? `<span class="eom-cart-title__brand">${brandLogo}</span>`
      : brand
        ? `<span class="eom-cart-title__brand eom-cart-title__brand-text">${escapeHtml(brand)}</span>`
        : '';

    return {
      seoTitle,
      html: `
        <span class="eom-cart-title" aria-hidden="true">
          <span class="eom-cart-title__header">
            ${brandMarkup}
            <span class="eom-cart-title__type">${escapeHtml(productType)}</span>
          </span>
          ${specs.length ? `<span class="eom-cart-title__specs">${specs.map((spec) => `<span class="eom-cart-title__spec">${escapeHtml(spec)}</span>`).join('')}</span>` : ''}
        </span>
      `
    };
  }

  function formatCartLineTitles() {
    if (!currentCart?.items?.length) return;
    const lineElements = findCartLineElements();
    const usedElements = new Set();

    currentCart.items.forEach((item, index) => {
      const lineElement = lineElements.find((element) => {
        return !usedElements.has(element) && cartLineMatchesItem(element, item, index);
      });
      if (!lineElement) return;
      usedElements.add(lineElement);

      const titleLink = lineElement.querySelector('a.cart-items__title')
        || lineElement.querySelector('.cart-items__product-info a[href*="/products/"]')
        || lineElement.querySelector('.cart-item__details a[href*="/products/"]');
      if (!(titleLink instanceof HTMLElement)) return;

      const { seoTitle, html } = buildCartTitleHtml(item);
      titleLink.setAttribute('aria-label', `View ${seoTitle}`);
      titleLink.setAttribute('title', seoTitle);
      titleLink.setAttribute('data-eom-seo-title', seoTitle);
      titleLink.classList.add('eom-cart-title-link');
      if (titleLink.parentElement?.tagName === 'P') titleLink.parentElement.classList.add('eom-cart-title-wrap');
      titleLink.innerHTML = html;
    });
  }

  function removeInjectedLineControls() {
    document.querySelectorAll('.eom-line-control').forEach((control) => {
      control.remove();
    });
  }

  function buildQuantityDropdown({ lineKey, kind, min, max, value, label }) {
    const selectedValue = Math.max(min, Math.min(max, Number(value || min)));
    const options = Array.from({ length: max - min + 1 }, (_, index) => {
      const optionValue = min + index;
      return `
        <li
          class="eom-cart-dropdown__item${optionValue === selectedValue ? ' is-selected' : ''}"
          data-eom-dropdown-option="${optionValue}"
        >${optionValue}</li>
      `;
    }).join('');
    const hiddenAttribute = kind === 'checkout' ? 'data-eom-select-qty' : 'data-eom-save-qty';

    return `
      <div class="eom-cart-dropdown" data-eom-cart-dropdown="${kind}" data-eom-line-key="${escapeHtml(lineKey)}">
        <input type="hidden" ${hiddenAttribute}="${escapeHtml(lineKey)}" value="${selectedValue}">
        <button type="button" class="eom-cart-dropdown__trigger" data-eom-dropdown-trigger aria-label="${escapeHtml(label)}">
          <span data-eom-dropdown-label>${selectedValue}</span>
          <svg class="eom-cart-dropdown__arrow" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"></path>
          </svg>
        </button>
        <ul class="eom-cart-dropdown__list">
          ${options}
        </ul>
      </div>
    `;
  }

  function setCartDropdownValue(dropdown, value) {
    if (!(dropdown instanceof Element)) return;
    const nextValue = String(value);
    const label = dropdown.querySelector('[data-eom-dropdown-label]');
    const hidden = dropdown.querySelector('input[type="hidden"]');
    if (label) label.textContent = nextValue;
    if (hidden) hidden.value = nextValue;
    dropdown.querySelectorAll('[data-eom-dropdown-option]').forEach((option) => {
      option.classList.toggle('is-selected', option.getAttribute('data-eom-dropdown-option') === nextValue);
    });
  }

  function injectLineControls() {
    if (rendering) return;
    if (!currentCart?.items?.length || !currentState?.overLimit) {
      removeInjectedLineControls();
      return;
    }

    rendering = true;
    try {
      removeInjectedLineControls();

      const lineElements = findCartLineElements();
      const usedElements = new Set();
      const selectedQuantities = readSelectedQuantities(currentCart);

      currentCart.items.forEach((item, index) => {
        const lineElement = lineElements.find((element) => {
          return !usedElements.has(element) && cartLineMatchesItem(element, item, index);
        });
        if (!lineElement) return;
        usedElements.add(lineElement);

        const lineQuantity = Number(item.quantity || 0);
        const selectedQuantity = Math.max(0, Math.min(lineQuantity, Number(selectedQuantities[item.key] || 0)));
        const defaultSaveQuantity = Math.max(1, lineQuantity - selectedQuantity);
        const checkoutQuantityControl = lineQuantity <= 1
          ? '<span class="eom-line-action">1 of 1</span>'
          : `
            <div class="eom-line-action">
              ${buildQuantityDropdown({
                lineKey: item.key,
                kind: 'checkout',
                min: 0,
                max: lineQuantity,
                value: selectedQuantity,
                label: 'Checkout quantity'
              })}
              <span>of ${lineQuantity}</span>
            </div>
          `;

        const control = document.createElement('div');
        control.className = 'eom-line-control';
        control.setAttribute('data-eom-line-control', item.key);
        control.innerHTML = `
          <div class="eom-line-control__row">
            <label class="eom-line-select">
              <input type="checkbox" data-eom-select-line="${escapeHtml(item.key)}"${selectedQuantity > 0 ? ' checked' : ''}>
              <span>Checkout now</span>
            </label>
            ${checkoutQuantityControl}
          </div>
          <div class="eom-line-control__row eom-line-save-row">
            <div class="eom-line-select">
              <span>Save for later</span>
            </div>
            <div class="eom-line-action">
              ${buildQuantityDropdown({
                lineKey: item.key,
                kind: 'save',
                min: 1,
                max: lineQuantity,
                value: defaultSaveQuantity,
                label: 'Save for later quantity'
              })}
              <button type="button" class="eom-line-save-link" data-eom-save-qty-line="${escapeHtml(item.key)}">Save</button>
            </div>
          </div>
        `;
        getControlTarget(lineElement).appendChild(control);
      });
    } finally {
      rendering = false;
    }
  }

  function lineControlsNeedInjection() {
    if (!currentCart?.items?.length || !currentState?.overLimit) {
      return document.querySelector('.eom-line-control') !== null;
    }

    const existingControls = Array.from(document.querySelectorAll('.eom-line-control'));
    if (existingControls.length < currentCart.items.length) return true;

    const existingKeys = new Set(existingControls.map((control) => control.getAttribute('data-eom-line-control')));
    return currentCart.items.some((item) => !existingKeys.has(item.key));
  }

  function renderAll(state = currentState) {
    ensureStyles();
    document.documentElement.classList.add('eom-checkout-gate-active');
    document.documentElement.classList.toggle('eom-checkout-gate-over-limit', Boolean(state?.overLimit));
    ensureFooterTools();
    updateProductAreaLayout(state);
    updateBanner(state);
    renderSavedPanel();
    formatCartLineTitles();
    normalizeAcceleratedCheckoutWallets();
    injectLineControls();
  }

  async function changeCartLine(lineKey, quantity) {
    const response = await nativeFetch('/cart/change.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity })
    });
    if (!response.ok) throw new Error(`Cart change failed. Status: ${response.status}`);
    return response.json();
  }

  async function addSavedItemToCart(item) {
    const payload = {
      id: item.variant_id,
      quantity: Number(item.quantity || 1)
    };
    if (item.properties && Object.keys(item.properties).length) payload.properties = item.properties;
    if (item.selling_plan) payload.selling_plan = item.selling_plan;

    const response = await nativeFetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`Cart add failed. Status: ${response.status}`);
    return response.json();
  }

  function requestCartUiRefresh() {
    document.dispatchEvent(new CustomEvent('cart:update'));
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
    scheduleRefresh('cart-ui-refresh', 150);
    scheduleRefresh('cart-ui-refresh-settled', 700);
  }

  function updateSelectedQuantity(lineKey, quantity) {
    if (!currentCart?.items?.length || !lineKey) return;
    const item = currentCart.items.find((line) => line.key === lineKey);
    if (!item) return;

    const maxLineQuantity = Number(item.quantity || 0);
    const selectedQuantity = Math.max(0, Math.min(maxLineQuantity, Number(quantity || 0)));
    const quantities = readSelectedQuantities(currentCart);
    quantities[lineKey] = selectedQuantity;
    writeSelectedQuantities(quantities);

    document.querySelectorAll('[data-eom-select-qty]').forEach((input) => {
      if (input.getAttribute('data-eom-select-qty') === lineKey) input.value = String(selectedQuantity);
    });
    document.querySelectorAll('[data-eom-cart-dropdown="checkout"]').forEach((dropdown) => {
      if (dropdown.getAttribute('data-eom-line-key') === lineKey) setCartDropdownValue(dropdown, selectedQuantity);
    });
    document.querySelectorAll('[data-eom-select-line]').forEach((checkbox) => {
      if (checkbox.getAttribute('data-eom-select-line') === lineKey) checkbox.checked = selectedQuantity > 0;
    });
    document.querySelectorAll('[data-eom-save-qty]').forEach((input) => {
      if (input.getAttribute('data-eom-save-qty') !== lineKey) return;
      const saveQuantity = Math.max(1, Math.min(maxLineQuantity, maxLineQuantity - selectedQuantity || 1));
      input.value = String(saveQuantity);
    });
    document.querySelectorAll('[data-eom-cart-dropdown="save"]').forEach((dropdown) => {
      if (dropdown.getAttribute('data-eom-line-key') !== lineKey) return;
      const saveQuantity = Math.max(1, Math.min(maxLineQuantity, maxLineQuantity - selectedQuantity || 1));
      setCartDropdownValue(dropdown, saveQuantity);
    });

    currentState = buildState(currentCart);
    updateBanner(currentState);
    dispatchState(currentState);
  }

  async function saveCartLine(lineKey, button) {
    if (busyAction) return;
    if (!currentCart) await refreshCartState('save-line-precheck');
    const item = currentCart?.items?.find((line) => line.key === lineKey);
    if (!item) return;

    busyAction = true;
    button?.classList.add('eom-action-busy');

    try {
      await changeCartLine(lineKey, 0);
      saveItemLocally(item);
      requestCartUiRefresh();
      await refreshCartState('save-for-later');
    } catch (error) {
      console.warn('[EOM checkout gate] Save for later failed.', error);
      window.alert('This item could not be saved for later. Please try again.');
    } finally {
      busyAction = false;
      button?.classList.remove('eom-action-busy');
    }
  }

  async function saveCartLineQuantity(lineKey, quantity, button) {
    if (busyAction) return;
    if (!currentCart) await refreshCartState('save-line-quantity-precheck');
    const item = currentCart?.items?.find((line) => line.key === lineKey);
    if (!item) return;

    const currentQuantity = Number(item.quantity || 0);
    const saveQuantity = Math.max(1, Math.min(currentQuantity, Number(quantity || 1)));
    const remainingQuantity = currentQuantity - saveQuantity;

    busyAction = true;
    button?.classList.add('eom-action-busy');

    try {
      await changeCartLine(lineKey, remainingQuantity);
      saveItemLocally({ ...item, quantity: saveQuantity });
      requestCartUiRefresh();
      await refreshCartState('save-for-later-quantity');
    } catch (error) {
      console.warn('[EOM checkout gate] Save quantity for later failed.', error);
      window.alert('This quantity could not be saved for later. Please try again.');
    } finally {
      busyAction = false;
      button?.classList.remove('eom-action-busy');
    }
  }

  async function restoreSavedItem(index, button) {
    if (busyAction) return;
    const saved = readSavedItems();
    const item = saved[index];
    if (!item) return;

    busyAction = true;
    button?.classList.add('eom-action-busy');

    try {
      const nextSaved = saved.filter((_, savedIndex) => savedIndex !== index);
      writeSavedItems(nextSaved);
      try {
        await addSavedItemToCart(item);
      } catch (error) {
        writeSavedItems(saved);
        throw error;
      }
      requestCartUiRefresh();
      refreshCartState('restore-saved-item');
    } catch (error) {
      console.warn('[EOM checkout gate] Restore saved item failed.', error);
      window.alert('This item could not be moved back to cart. Please try again.');
    } finally {
      busyAction = false;
      button?.classList.remove('eom-action-busy');
    }
  }

  async function checkoutSelected(button) {
    if (busyAction) return;
    if (!currentCart) await refreshCartState('checkout-selected-precheck');

    const selected = getSelectedSummary(currentCart);
    if (selected.count < 1) {
      showModal('Select items to checkout', `Select at least 1 item and no more than ${maxQuantity} items.`, false);
      return;
    }
    if (selected.count > maxQuantity) {
      showModal('Too many selected items', `Selected quantity is ${selected.count}. Select ${maxQuantity} or fewer items.`, false);
      return;
    }

    const selectedQuantities = selected.quantities || {};
    const changes = [];
    const itemsToSave = [];

    currentCart.items.forEach((item) => {
      const currentQuantity = Number(item.quantity || 0);
      const checkoutQuantity = Math.max(0, Math.min(currentQuantity, Number(selectedQuantities[item.key] || 0)));
      const savedQuantity = currentQuantity - checkoutQuantity;

      if (savedQuantity > 0) {
        itemsToSave.push({ ...item, quantity: savedQuantity });
      }

      if (checkoutQuantity !== currentQuantity) {
        changes.push({ key: item.key, quantity: checkoutQuantity });
      }
    });

    if (!changes.length) {
      window.location.href = '/checkout';
      return;
    }

    busyAction = true;
    button?.classList.add('eom-action-busy');

    try {
      for (const change of changes) {
        await changeCartLine(change.key, change.quantity);
      }
      saveItemsLocally(itemsToSave);
      writeSelectedQuantities({});
      window.location.href = '/checkout';
    } catch (error) {
      console.warn('[EOM checkout gate] Checkout selected failed.', error);
      window.alert('Checkout selected could not be prepared. Please try again.');
      await refreshCartState('checkout-selected-failed');
      busyAction = false;
      button?.classList.remove('eom-action-busy');
    }
  }

  function startObserver() {
    if (observer) return;
    observer = new MutationObserver(() => {
      if (!currentState || rendering) return;
      if (document.querySelector('[data-eom-cart-dropdown].is-open')) return;
      window.clearTimeout(observerTimer);
      observerTimer = window.setTimeout(() => {
        if (rendering) return;
        ensureFooterTools();
        const banner = document.getElementById('eom-checkout-gate-banner');
        const header = getCartHeaderContainer();
        if (!banner || (header && banner.parentElement !== header)) updateBanner(currentState);
        if (!document.getElementById('eom-save-for-later-panel')) renderSavedPanel();
        updateCheckoutControls(currentState);
        updateProductAreaLayout(currentState);
        formatCartLineTitles();
        normalizeAcceleratedCheckoutWallets();
        if (lineControlsNeedInjection()) injectLineControls();
      }, 80);
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  function isCheckoutClickTarget(target) {
    if (!(target instanceof Element)) return false;
    const control = target.closest(checkoutSelector);
    if (!control) return false;
    return !control.closest('[data-eom-checkout-gate-ignore]');
  }

  function isCheckoutSubmit(form, submitter) {
    if (!(form instanceof HTMLFormElement)) return false;
    const action = form.getAttribute('action') || '';
    const submitterName = submitter?.getAttribute?.('name') || '';
    const submitterAction = submitter?.getAttribute?.('formaction') || '';

    return action.includes('/checkout')
      || submitterAction.includes('/checkout')
      || submitterName === 'checkout'
      || submitter?.matches?.('button#checkout, input#checkout');
  }

  function blockCheckoutEvent(event, state = currentState) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    updateBanner(state);

    if (state?.selectedAllowed) checkoutSelected(event.submitter || event.target?.closest?.(checkoutSelector));
    else showBlockedModal(state);

    document.dispatchEvent(new CustomEvent('eom:checkout-gate:blocked', { detail: state }));
  }

  window.fetch = async function eomCheckoutGateFetch(input, init = {}) {
    const response = await nativeFetch(input, init);
    if (isCartMutationUrl(input)) {
      scheduleRefresh('cart-fetch-mutation', 150);
      scheduleRefresh('cart-fetch-mutation-settled', 700);
    }
    return response;
  };

  document.addEventListener('input', (event) => {
    const quantityInput = event.target instanceof Element ? event.target.closest('[data-eom-select-qty]') : null;
    if (!quantityInput) return;
    updateSelectedQuantity(
      quantityInput.getAttribute('data-eom-select-qty'),
      Number(quantityInput.value || 0)
    );
  });

  ['pointerdown', 'mousedown', 'touchstart'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.closest('.eom-line-control, #eom-save-for-later-panel')) return;
      event.stopPropagation();
      event.stopImmediatePropagation();
    }, true);
  });

  document.addEventListener('change', (event) => {
    const checkbox = event.target instanceof Element ? event.target.closest('[data-eom-select-line]') : null;
    const quantityInput = event.target instanceof Element ? event.target.closest('[data-eom-select-qty]') : null;

    if (quantityInput) {
      event.preventDefault();
      updateSelectedQuantity(
        quantityInput.getAttribute('data-eom-select-qty'),
        Number(quantityInput.value || 0)
      );
      return;
    }

    if (!checkbox) return;

    const lineKey = checkbox.getAttribute('data-eom-select-line');
    const item = currentCart?.items?.find((line) => line.key === lineKey);
    updateSelectedQuantity(lineKey, checkbox.checked ? Number(item?.quantity || 0) : 0);
  });

  document.addEventListener('click', (event) => {
    const dropdownOption = event.target instanceof Element ? event.target.closest('[data-eom-dropdown-option]') : null;
    if (dropdownOption) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const dropdown = dropdownOption.closest('[data-eom-cart-dropdown]');
      const lineKey = dropdown?.getAttribute('data-eom-line-key');
      const value = Number(dropdownOption.getAttribute('data-eom-dropdown-option') || 0);
      setCartDropdownValue(dropdown, value);
      dropdown?.classList.remove('is-open');
      if (dropdown?.getAttribute('data-eom-cart-dropdown') === 'checkout') updateSelectedQuantity(lineKey, value);
      return;
    }

    const dropdownTrigger = event.target instanceof Element ? event.target.closest('[data-eom-dropdown-trigger]') : null;
    if (dropdownTrigger) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const dropdown = dropdownTrigger.closest('[data-eom-cart-dropdown]');
      document.querySelectorAll('[data-eom-cart-dropdown]').forEach((otherDropdown) => {
        if (otherDropdown !== dropdown) otherDropdown.classList.remove('is-open');
      });
      dropdown?.classList.toggle('is-open');
      return;
    }

    const saveQuantityButton = event.target instanceof Element ? event.target.closest('[data-eom-save-qty-line]') : null;
    if (saveQuantityButton) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const lineKey = saveQuantityButton.getAttribute('data-eom-save-qty-line');
      const quantityInput = Array.from(document.querySelectorAll('[data-eom-save-qty]')).find((input) => {
        return input.getAttribute('data-eom-save-qty') === lineKey;
      });
      saveCartLineQuantity(lineKey, Number(quantityInput?.value || 1), saveQuantityButton);
      return;
    }

    const saveButton = event.target instanceof Element ? event.target.closest('[data-eom-save-line]') : null;
    if (saveButton) {
      event.preventDefault();
      saveCartLine(saveButton.getAttribute('data-eom-save-line'), saveButton);
      return;
    }

    const restoreButton = event.target instanceof Element ? event.target.closest('[data-eom-restore-saved]') : null;
    if (restoreButton) {
      event.preventDefault();
      restoreSavedItem(Number(restoreButton.getAttribute('data-eom-restore-saved')), restoreButton);
      return;
    }

    const removeSavedButton = event.target instanceof Element ? event.target.closest('[data-eom-remove-saved]') : null;
    if (removeSavedButton) {
      event.preventDefault();
      removeSavedItem(Number(removeSavedButton.getAttribute('data-eom-remove-saved')));
      renderSavedPanel();
      return;
    }

    if (!(event.target instanceof Element) || !event.target.closest('[data-eom-cart-dropdown]')) {
      document.querySelectorAll('[data-eom-cart-dropdown].is-open').forEach((dropdown) => {
        dropdown.classList.remove('is-open');
      });
    }

    if (!isCheckoutClickTarget(event.target)) return;
    if (currentState?.overLimit) blockCheckoutEvent(event, currentState);
  }, true);

  document.addEventListener('submit', (event) => {
    if (!isCheckoutSubmit(event.target, event.submitter)) return;
    if (currentState?.overLimit) blockCheckoutEvent(event, currentState);
  }, true);

  document.addEventListener('cart:update', () => scheduleRefresh('cart:update-event', 100));
  document.addEventListener('cart:updated', () => scheduleRefresh('cart:updated-event', 100));
  document.addEventListener('ajaxProduct:added', () => scheduleRefresh('ajaxProduct:added-event', 100));
  document.addEventListener('eom:save-for-later:updated', () => renderSavedPanel());

  window.EOMCheckoutGate = {
    refresh: refreshCartState,
    getState: () => currentState,
    getCart: () => currentCart,
    getSavedItems: readSavedItems,
    clearSavedItems: () => {
      writeSavedItems([]);
      renderSavedPanel();
    },
    getSelectedSummary: () => getSelectedSummary(currentCart),
    clearSelected: () => {
      writeSelectedKeys([]);
      refreshCartState('clear-selected');
    },
    maxQuantity
  };

  document.addEventListener('DOMContentLoaded', () => {
    ensureStyles();
    startObserver();
    renderSavedPanel();
    normalizeAcceleratedCheckoutWallets();
    refreshCartState('dom-ready');
    window.setTimeout(normalizeAcceleratedCheckoutWallets, 700);
  });

  window.addEventListener('pageshow', () => {
    ensureStyles();
    startObserver();
    renderSavedPanel();
    normalizeAcceleratedCheckoutWallets();
    refreshCartState('pageshow');
    window.setTimeout(normalizeAcceleratedCheckoutWallets, 700);
  });

  ensureStyles();
  startObserver();
  renderSavedPanel();
  normalizeAcceleratedCheckoutWallets();
  refreshCartState('script-load');
  window.setTimeout(normalizeAcceleratedCheckoutWallets, 700);
})();